/// Theme-håndtering: dark (default) vs. light. Persist'er valg i
/// localStorage så det er stabilt på tværs af sessions. Hvis intet er gemt,
/// følger vi OS-preference (`prefers-color-scheme`).
///
/// Anvendelse: ThemeService instans i `main.ts`, plus en lille INLINE
/// pre-load script i `<head>` (i `_Layout.cshtml`) der sætter klassen
/// FØR resten af siden renderer — ellers ville brugeren se et flash
/// af det forkerte tema før admin.js har bootstrappet.

export type Theme = 'dark' | 'light';

export interface IThemeService {
  current(): Theme;
  toggle(): void;
  set(theme: Theme): void;
}

export class ThemeService implements IThemeService {
  private static readonly STORAGE_KEY = 'swp-admin-theme';
  private theme: Theme;

  constructor(private readonly doc: Document = document) {
    this.theme = ThemeService.detect();
    this.apply(this.theme);
  }

  current(): Theme {
    return this.theme;
  }

  toggle(): void {
    this.set(this.theme === 'dark' ? 'light' : 'dark');
  }

  set(theme: Theme): void {
    this.theme = theme;
    this.apply(theme);
    try {
      localStorage.setItem(ThemeService.STORAGE_KEY, theme);
    } catch {
      // localStorage kan være disabled (private mode); fail silent —
      // theme-valget er da bare ikke persistent indtil næste reload.
    }
  }

  private apply(theme: Theme): void {
    this.doc.documentElement.classList.toggle('theme-light', theme === 'light');

    // Ikon-state: sun = "skift til dark"; moon = "skift til light".
    const icon = theme === 'dark' ? 'ph-sun' : 'ph-moon';
    this.doc.querySelectorAll<HTMLElement>('[data-theme-icon]').forEach(el => {
      el.innerHTML = `<i class="ph ${icon}"></i>`;
    });

    // Notifiér interesserede services (charts mv.) at CSS-vars har skiftet.
    window.dispatchEvent(new CustomEvent('theme-changed', { detail: { theme } }));
  }

  /// Statisk så pre-load-script i `<head>` kan kalde samme detection
  /// uden at instantiere klassen.
  static detect(): Theme {
    try {
      const saved = localStorage.getItem(ThemeService.STORAGE_KEY);
      if (saved === 'dark' || saved === 'light') return saved;
    } catch {
      // ignore
    }
    return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
}
